/**
 * AI Menu Import Page Component
 * Main component for AI-powered menu import flow
 */

import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { useDepartments } from '@hooks/menu';
import { selectEffectiveOutletId } from '@store/outlets';
import {
  fetchPendingExtractions,
  selectCurrentExtraction,
  selectHasPendingExtraction,
  setCurrentExtraction,
} from '@store/menuExtractions';
import { AlertCircle, ArrowLeft, Sparkles } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { MenuExtractionReview } from './MenuExtractionReview';
import { MenuImportUpload } from './MenuImportUpload';

type ImportStep = 'upload' | 'review';

interface AIMenuImportProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

export const AIMenuImport: React.FC<AIMenuImportProps> = ({
  onComplete,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectEffectiveOutletId);
  const { data: allDepartments = [] } = useDepartments(outletId);
  const departments = allDepartments.filter((d) => d.status === 'active');
  const currentExtraction = useAppSelector(selectCurrentExtraction);
  const hasPendingExtraction = useAppSelector(selectHasPendingExtraction);

  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');

  // Fetch pending extractions on mount
  useEffect(() => {
    if (outletId) {
      dispatch(fetchPendingExtractions(outletId));
    }
  }, [dispatch, outletId]);

  // Auto-switch to review if there's a pending extraction with results
  useEffect(() => {
    if (hasPendingExtraction && currentExtraction?.json_output) {
      setCurrentStep('review');
    }
  }, [hasPendingExtraction, currentExtraction?.json_output]);

  const handleExtractionComplete = () => {
    setCurrentStep('review');
  };

  const handleReviewComplete = () => {
    // Reset to upload step and clear current extraction
    dispatch(setCurrentExtraction(null));
    setCurrentStep('upload');
    onComplete?.();
  };

  const handleCancel = () => {
    dispatch(setCurrentExtraction(null));
    setCurrentStep('upload');
    onCancel?.();
  };

  if (!outletId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-gray-500">
          <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
          <p>No outlet selected. Please select an outlet first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onCancel && (
            <button
              type="button"
              onClick={handleCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={24} className="text-pos-primary" />
              <h2 className="text-xl font-semibold text-gray-900">
                AI Menu Import
              </h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {currentStep === 'upload'
                ? 'Upload your menu file and let AI extract it for you'
                : 'Review and confirm the extracted menu items'}
            </p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
              currentStep === 'upload'
                ? 'bg-pos-primary text-white'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">
              1
            </span>
            <span>Upload</span>
          </div>
          <div className="w-8 h-0.5 bg-gray-200" />
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
              currentStep === 'review'
                ? 'bg-pos-primary text-white'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">
              2
            </span>
            <span>Review</span>
          </div>
        </div>
      </div>

      {/* Department Warning */}
      {departments.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle
            size={20}
            className="text-amber-500 flex-shrink-0 mt-0.5"
          />
          <div>
            <p className="font-medium text-amber-800">Departments Required</p>
            <p className="text-sm text-amber-700 mt-1">
              Before importing your menu, please create at least one department
              (e.g., Kitchen, Bar) in the Departments section. This is required
              for proper menu item routing.
            </p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {currentStep === 'upload' && (
          <MenuImportUpload onExtractionComplete={handleExtractionComplete} />
        )}
        {currentStep === 'review' && (
          <MenuExtractionReview
            onComplete={handleReviewComplete}
            onCancel={handleCancel}
          />
        )}
      </div>
    </div>
  );
};
